/**
 * Types for Drug Substance Dose API responses
 * These match the official GekkoVet API schema (snake_case)
 */

// Source reference
export interface Source {
  id: number;
  name: string;
}

// Dose type (General, Loading, Maintenance, etc.)
export interface DoseType {
  id: number;
  name: string;
}

// Drug unit (mg, ml, etc.)
export interface DrugUnitType {
  id: number;
  name: string;
}

// Dosage interval type (q12h, BID, etc.)
export interface DosageIntervalType {
  id: number;
  name: string;
}

// Treatment duration type (days, weeks, etc.)
export interface TreatmentDurationType {
  id: number;
  name: string;
}

// Treatment duration prefix (for, up to, etc.)
export interface TreatmentDurationPrefix {
  id: number;
  name: string;
}

// Drug administration route (PO, IV, SC, etc.)
export interface DrugAdministrationRoute {
  id: number;
  name: string;
}

// Drug indication (Hypoglycemia, Pain, etc.)
export interface DrugIndication {
  id: number;
  name: string;
}

// A single phase within a dose alternative
export interface DosePhase {
  id: number;
  dose_type: DoseType;
  dose: number;
  dose_max?: number | null;
  drug_unit: DrugUnitType;
  dosage_interval?: number | null;
  dosage_interval_max?: number | null;
  dosage_interval_type: DosageIntervalType;
  treatment_duration?: number | null;
  treatment_duration_max?: number | null;
  treatment_duration_type: TreatmentDurationType;
  treatment_duration_prefix?: TreatmentDurationPrefix | null;
  drug_administration_route: DrugAdministrationRoute;
}

// A dose alternative (contains multiple phases)
export interface DoseAlternative {
  id?: number;
  sources: Source[];
  phases: DosePhase[];
}

// Drug substance dose (contains indication and alternatives)
export interface DrugSubstanceDose {
  id: number;
  dosage_specification?: string | null;
  drug_indication: DrugIndication;
  sources: Source[];
  info?: string | null;
  alternatives: DoseAlternative[];
}

// Drug substance basic info
export interface DrugSubstanceBasic {
  id: number;
  name: string;
}

// Drug substance with full info
export interface DrugSubstanceInfo {
  id: number;
  name: string;
  info?: string | null;
  sources: Source[];
}

// Paged response from API
export interface PagedDrugSubstanceDoseResponse {
  current_page: number;
  total_pages: number;
  total_items: number;
  page_size: number;
  items: DrugSubstanceDose[];
}

// Helper functions for formatting dose information

/**
 * Format dose string (e.g., "0.50 - 1.00 ml/kg")
 */
export const formatDose = (phase: DosePhase): string => {
  const doseMin = phase.dose;
  const doseMax = phase.dose_max;
  const drugUnit = phase.drug_unit.name;

  if (!doseMax) {
    return `${doseMin} ${drugUnit}`;
  }
  return `${doseMin} - ${doseMax} ${drugUnit}`;
};

/**
 * Format interval string (e.g., "once/single dose" or "12 - 24 hours")
 */
export const formatInterval = (phase: DosePhase): string => {
  const intervalType = phase.dosage_interval_type.name;
  const intervalMin = phase.dosage_interval;
  const intervalMax = phase.dosage_interval_max;

  if (intervalMin && !intervalMax) {
    return `${intervalMin} ${intervalType}`;
  }
  if (intervalMin && intervalMax) {
    return `${intervalMin} - ${intervalMax} ${intervalType}`;
  }
  return intervalType;
};

/**
 * Format duration string (e.g., "for 7 - 14 days" or "single dose")
 */
export const formatDuration = (phase: DosePhase): string => {
  const durationType = phase.treatment_duration_type.name;
  const durationMin = phase.treatment_duration;
  const durationMax = phase.treatment_duration_max;
  const prefix = phase.treatment_duration_prefix?.name
    ? `${phase.treatment_duration_prefix.name} `
    : '';

  if (durationMin && !durationMax) {
    return `${prefix}${durationMin} ${durationType}`;
  }
  if (durationMin && durationMax) {
    return `${prefix}${durationMin} - ${durationMax} ${durationType}`;
  }
  return durationType ? `${prefix}${durationType}` : 'not specified';
};
